import { cn } from '@/lib/utils';
import TooltipWrapper from '@/components/tooltip/TooltipWrapper';
import useTranslation from '@/hooks/useTranslation';
import { ConversationQualitySignals } from '@/types';

interface QualityBadgeProps {
  quality?: ConversationQualitySignals | null;
  className?: string;
}

/**
 * QualityBadge — TELV1-433.
 *
 * Conversation-level quality category pill (Excellent / Good / Low),
 * fed by the LLM-judged signals the analyzer attaches to each
 * conversation. Hovering reveals the judge's one-sentence reason — the
 * "why" without opening the transcript (the TEL-1297 display contract,
 * shared with V2 so both platforms speak one quality vocabulary).
 *
 * Renders `/` (the table's standard absent-value glyph) for anything
 * not yet scored — pending, failed, insufficient data — so the column
 * never needs translated disclaimers.
 */
const categoryStyles: Record<string, string> = {
  EXCELLENT: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  GOOD: 'bg-neutral-50 text-neutral-700 border-neutral-200',
  LOW: 'bg-red-50 text-red-700 border-red-200',
};

const categoryLabelKeys: Record<string, string> = {
  EXCELLENT: 'msg.qualityExcellent',
  GOOD: 'msg.qualityGood',
  LOW: 'msg.qualityLow',
};

function QualityBadge({ quality, className }: QualityBadgeProps) {
  const { lang } = useTranslation();

  const category = quality?.status === 'complete' ? quality.category : undefined;
  if (!category || !categoryStyles[category]) {
    return <span className="text-neutral-400">/</span>;
  }

  const pill = (
    <span
      className={cn(
        'inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium border whitespace-nowrap',
        categoryStyles[category],
        className,
      )}
    >
      {lang.get(categoryLabelKeys[category])}
      {quality?.overridden && <span aria-hidden="true">*</span>}
    </span>
  );

  // Overridden rows explain BOTH judgements: the researcher's reason and
  // what the AI originally said. Plain judged rows show the judge's reason.
  const tooltip = quality?.overridden
    ? `${lang.get('msg.qualityAdjustedByResearcher')}${quality.override_reason ? ` — ${quality.override_reason}` : ''}. ${lang.get('msg.qualityAIJudged')}: ${quality.ai_category ? lang.get(categoryLabelKeys[quality.ai_category]) : ''}${quality.reason ? ` — ${quality.reason}` : ''}`
    : quality?.reason;

  if (!tooltip) return pill;

  return (
    <TooltipWrapper text={tooltip} className="max-w-[420px] text-xs z-[100]">
      {pill}
    </TooltipWrapper>
  );
}

export default QualityBadge;
